import type { FromBasePlayerInput, FromBasePlayerMetadata, FromBaseRow } from './from-base-context';
import { createFromBasePlayerContext, fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import { canonicalizeCreatureSizeCells } from './creature-size';
import { compileCreatureDisplayName } from './creature-display-name';
import { constructPlayerDefinitionPictures } from './from-base-player-media';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { readLayerListEntryUuid } from './inventory-raw-readers';

/** The PC finalizer window BEFORE preliminary essences. Monster/swarm sources
 * still need their own captured construction; they never silently compile as a plain PC. */
export function constructPlayerClassificationFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerMetadata, prefix: FromBaseRow,
    context = createFromBasePlayerContext(base, metadata)): FromBaseRow {
    const { readers, definition } = context;
    const host = fromBaseClone(prefix), ec = base.encounter_creatures ?? {};
    const field = (row: FromBaseRow, key: string) => readers.layerValue(row, key);
    if (phpJsonTruthy(field(ec, 'encounter_creature_monster_template_id')))
        throw new FromBaseStageMissing('classification-template-swarm-media', 'player-monster-template-override');
    if (Object.values(readers.layerEntries(host, 'creature_template')).length)
        throw new FromBaseStageMissing('classification-template-swarm-media', 'player-swarm-template');
    const stringOrNull = (value: unknown) => value == null ? null : phpJsonString(value);
    (host.items as FromBaseRow).item_name = compileCreatureDisplayName({
        playerName: stringOrNull(field(base.player_characters, 'player_name')),
        encounterCreatureName: stringOrNull(field(ec, 'encounter_creature_name')),
        itemName: stringOrNull(readers.itemField(host, 'item_name')),
    });
    // Selected ancestry seeds the host taxonomy. Retain provider-granted subtype
    // edges too (e.g. Netherbound -> Apparitions), so own-source compute clusters
    // see the same carriers as NPCs without flattening their authored effects.
    const subtypes = [...new Set(Object.values(readers.layerEntries(host, 'creature_sub_type'))
        .map(readLayerListEntryUuid).filter(fromBaseUuid))];
    if (subtypes.length) (host.item_layer_types as FromBaseRow).creature_sub_type = subtypes;
    const modelId = field(host, 'creature_model');
    if (fromBaseUuid(modelId)) {
        const model = definition(modelId);
        if (!model || !fromBaseRecord(model) || !Array.isArray(model.pictures))
            throw new FromBaseInputMissing('creature-model-pictures', modelId);
        const pictures = constructPlayerDefinitionPictures(model, metadata.cdnUrl, readers.layerValue);
        if (pictures.length) {
            const bucket = fromBaseRecord(host.pictures) ? host.pictures : {};
            host.pictures = { ...bucket, item: pictures };
            for (const key of ['item_picture', 'item_pictures', 'item_videos', 'item_video']) delete host[key];
        }
    }
    const size = canonicalizeCreatureSizeCells(Object.values(readers.layerEntries(host, 'size')));
    if (size !== null) (host.item_layer_types as FromBaseRow).size = metadata.layerCatalog.size?.is_multiple ? [size] : size;
    return host;
}
